<template>
    <div class="complexity-section">
        <div class="section-divider">
            <span class="section-title">Program Complexity</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">CPI Depth by Program</div>
                    <div class="panel-subtitle">Cross-program invocation depth (higher = more complex)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="cpiDepthChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Accounts per Transaction</div>
                    <div class="panel-subtitle">Average accounts touched per tx</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="accountsChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatPubkey, programLabel } from '../../composables/useFormatters';
import { colors, getBarChartOptions } from '../../composables/useChartConfig';

export default {
    name: 'ProgramComplexity',

    props: {
        programs: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            charts: {},
            colors: colors
        };
    },

    watch: {
        programs: {
            handler() {
                if (Object.keys(this.charts).length > 0) {
                    this.updateCharts();
                }
            },
            deep: true
        }
    },

    mounted() {
        this.initCharts();
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
    },

    methods: {
        formatPubkey,
        programLabel,

        initCharts() {
            // CPI Depth Chart
            this.charts.cpiDepth = markRaw(new Chart(this.$refs.cpiDepthChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    horizontal: true,
                    showLegend: false
                })
            }));

            // Accounts Chart
            this.charts.accounts = markRaw(new Chart(this.$refs.accountsChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    horizontal: true,
                    showLegend: false
                })
            }));

            this.updateCharts();
        },

        updateCharts() {
            if (!this.programs.length) return;

            // CPI Depth - sort by depth descending
            const byCpi = [...this.programs]
                .sort((a, b) => b.metrics.avgCpiDepth - a.metrics.avgCpiDepth)
                .slice(0, 10);

            this.charts.cpiDepth.data = {
                labels: byCpi.map(p => programLabel(p)),
                datasets: [{
                    data: byCpi.map(p => p.metrics.avgCpiDepth),
                    backgroundColor: this.colors.series[0],
                    borderWidth: 0
                }]
            };
            this.charts.cpiDepth.update();

            // Accounts per tx - sort by accounts descending
            const byAccounts = [...this.programs]
                .sort((a, b) => b.metrics.avgAccountsPerTx - a.metrics.avgAccountsPerTx)
                .slice(0, 10);

            this.charts.accounts.data = {
                labels: byAccounts.map(p => programLabel(p)),
                datasets: [{
                    data: byAccounts.map(p => p.metrics.avgAccountsPerTx),
                    backgroundColor: this.colors.series[2], // cyan
                    borderWidth: 0
                }]
            };
            this.charts.accounts.update();
        }
    }
};
</script>

<style scoped>
.chart-tall {
    min-height: 280px;
}
</style>
